import ko from 'knockout';
import moment from 'moment';
import { CustomError, customFetch as fetch } from '../../fetch';
import { errorToast, successToast } from '../../toastr-helper';
import EmployeeVacationRequestType from "../EmployeeVacationRequestType";
import { closePopups } from '../../init';

export class EmployeeVacationRequestModel {
    id: KnockoutObservable<string>;
    employeeId: KnockoutObservable<string>;
    avatar: KnockoutObservable<string>;
    employeeName: KnockoutObservable<string>;
    from: KnockoutObservable<moment.Moment>;
    to: KnockoutObservable<moment.Moment>;
    hours: KnockoutObservable<number>;
    statusValue: KnockoutObservable<number>;
    status: KnockoutObservable<string>;
    created: KnockoutObservable<moment.Moment>;
    reason: KnockoutObservable<string>;
    type: KnockoutObservable<number>;

    constructor(model: any = {}) {
        this.id = ko.observable<string>(model.id);
        this.employeeId = ko.observable<string>(model.employeeId);
        this.avatar = ko.observable<string>(model.avatar);
        this.employeeName = ko.observable<string>(model.employeeName);
        this.from = ko.observable<moment.Moment>(moment(model.from));
        this.to = ko.observable<moment.Moment>(moment(model.to));
        this.hours = ko.observable<number>(model.hours);
        this.statusValue = ko.observable<number>(model.statusValue);
        this.status = ko.observable<string>(model.status);
        this.created = ko.observable<moment.Moment>(moment(model.created));
        this.reason = ko.observable<string>(model.reason);
        this.type = ko.observable<number>(model.type);
    }
}

interface EmployeeVacationOverviewOptions {
    requestsInYearChanged: () => void;
    refresh?: () => void;
    getOverviewLocation: string;
    employeeId?: string;
}

export class AfasVacationBalanceModel {
    year : KnockoutObservable<number>;
    availableStatutoryHours: KnockoutObservable<number>;
    availableExtraStatutoryHours: KnockoutObservable<number>;
    startAvailableStatutoryHours: KnockoutObservable<number>;
    startAvailableExtraStatutoryHours: KnockoutObservable<number>;
    previousYearsSavedHours: KnockoutObservable<number>;
    previousYearsSavedExtraHours: KnockoutObservable<number>;
    currentYearAddedHours: KnockoutObservable<number>;
    currentYearAddedExtraHours: KnockoutObservable<number>;

    constructor(data: any) {
        this.year = ko.observable<number>(data.year);
        this.availableStatutoryHours = ko.observable<number>(data.availableStatutoryHours);
        this.availableExtraStatutoryHours = ko.observable<number>(data.availableExtraStatutoryHours);
        this.startAvailableStatutoryHours = ko.observable<number>(data.startAvailableStatutoryHours);
        this.startAvailableExtraStatutoryHours = ko.observable<number>(data.startAvailableExtraStatutoryHours);
        this.previousYearsSavedHours = ko.observable<number>(data.previousYearsSavedHours);
        this.previousYearsSavedExtraHours = ko.observable<number>(data.previousYearsSavedExtraHours);
        this.currentYearAddedHours = ko.observable<number>(data.currentYearAddedHours);
        this.currentYearAddedExtraHours = ko.observable<number>(data.currentYearAddedExtraHours);
    }
}

export class EmployeeVacationOverviewModel {
    year: KnockoutObservable<number>;

    hoursPerYear: KnockoutObservable<number>;
    hoursPerWeek: KnockoutObservable<number>;
    hoursWorkWeek: KnockoutObservable<number>;
    contractStart: KnockoutObservable<moment.Moment>;
    contractEnd: KnockoutObservable<moment.Moment>;
    requests: KnockoutObservableArray<EmployeeVacationRequestModel>;
    allAvailableHours: KnockoutObservable<Map<number, number>>;
    isPayrollOrFlexEmployee: KnockoutObservable<boolean>;

    hasAfasBalances: KnockoutObservable<boolean>;
    allAfasVacationBalances: KnockoutObservableArray<AfasVacationBalanceModel>;
    afasVacationBalances: KnockoutComputed<AfasVacationBalanceModel[]>;

    years: KnockoutComputed<number[]>;
    workWeekRatio: KnockoutComputed<number>;
    totalHours: KnockoutComputed<number>;
    availableHours: KnockoutComputed<number>;
    usedHours: KnockoutComputed<number>;
    pendingHours: KnockoutComputed<number>;
    extraHours: KnockoutComputed<number>;
    specialLeaveHours: KnockoutComputed<number>;
    requestsInYear: KnockoutComputed<EmployeeVacationRequestModel[]>;

    totalHoursNotExtra: KnockoutComputed<number>;
    totalHoursExtra: KnockoutComputed<number>;
    availableHoursNotExtra: KnockoutComputed<number>;
    availableHoursExtra: KnockoutComputed<number>;

    options: EmployeeVacationOverviewOptions;


    constructor(options: EmployeeVacationOverviewOptions) {
        const self = this;

        this.hoursPerYear = ko.observable<number>();
        this.hoursPerWeek = ko.observable<number>();
        this.hoursWorkWeek = ko.observable<number>();
        this.contractStart = ko.observable<moment.Moment>();
        this.contractEnd = ko.observable<moment.Moment>();
        this.requests = ko.observableArray<EmployeeVacationRequestModel>();
        this.allAvailableHours = ko.observable<Map<number, number>>(new Map<number, number>());
        this.year = ko.observable<number>(moment().year());
        this.allAfasVacationBalances = ko.observableArray<AfasVacationBalanceModel>(null);
        this.hasAfasBalances = ko.observable<boolean>(false);
        this.isPayrollOrFlexEmployee = ko.observable<boolean>(false);

        this.afasVacationBalances = ko.pureComputed(() =>
            this.hasAfasBalances()
                ? this.allAfasVacationBalances().filter((b) => b.year() === this.year())
                : []);
        
        this.requestsInYear = ko.pureComputed(() => this.year() ?
            this.requests().filter((r) => r.from().year() === this.year()) : this.requests());

        this.years = ko.pureComputed<number[]>(() => {
            if (!this.contractStart()) {
                return [];
            }

            const startYear = this.contractStart().year();
            const endYear = this.contractEnd()
                ? this.contractEnd().year()
                : moment().year() + 1;
            const result = [...Array(1 + (endYear - startYear)).keys()].map(i => endYear - i);
            return result;
        });

        this.extraHours = ko.pureComputed(() => {
            return this.requestsInYear().filter((r) => r.hours() < 0 && r.type() !== EmployeeVacationRequestType.SpecialLeave).reduce((s, r) => {
                return s + Math.abs(r.hours());
            }, 0);
        });
        this.usedHours = ko.pureComputed<number>(() => {
            return this.requestsInYear().filter((r) => r.statusValue() === 1 && r.hours() >= 0 && r.type() !== EmployeeVacationRequestType.SpecialLeave).reduce((s, r) => {
                return s + r.hours();
            }, 0);
        });
        this.pendingHours = ko.pureComputed<number>(() => this.hoursInState(0));
        this.availableHours = ko.pureComputed<number>(() => this.totalHours() - this.usedHours() + this.extraHours());
        this.workWeekRatio = ko.pureComputed<number>(() => this.hoursPerWeek() / this.hoursWorkWeek());
        this.totalHours = ko.pureComputed<number>(() => {
            if (this.allAvailableHours().has(this.year())) {
                return this.allAvailableHours().get(this.year())!;
            }

            return 0;
        });

        this.totalHoursNotExtra = ko.pureComputed<number>(() => {
            if (this.hasAfasBalances()) {
                return this.afasVacationBalances()
                    .reduce((result, b) => b.startAvailableStatutoryHours() + result, 0);
            }

            return 0;
        });

        this.totalHoursExtra = ko.pureComputed<number>(() => {
            if (this.hasAfasBalances()) {
                return this.afasVacationBalances()
                    .reduce((result, b) => b.startAvailableExtraStatutoryHours() + result, 0);
            }

            return 0;
        });

        this.availableHoursNotExtra = ko.pureComputed<number>(() => {
            if (this.hasAfasBalances()) {
                return this.afasVacationBalances()
                    .reduce((result, b) => b.availableStatutoryHours() + result, 0);
            }

            return 0;
        });

        this.availableHoursExtra = ko.pureComputed<number>(() => {
            if (this.hasAfasBalances()) {
                return this.afasVacationBalances()
                    .reduce((result, b) => b.availableExtraStatutoryHours() + result, 0);
            }

            return 0;
        });

        this.specialLeaveHours = ko.pureComputed<number>(() =>
            this.requestsInYear()
                .filter(r => r.statusValue() === 1
                    && r.hours() >= 0
                    && r.type() === EmployeeVacationRequestType.SpecialLeave)
                .reduce((s, r) => s + r.hours(), 0));

        this.options = options;

        this.requestsInYear.subscribe(() => {
            this.options.requestsInYearChanged();
        });
    }

    private hoursInState(state: number): number {
        return this.requestsInYear().filter((r) => r.statusValue() === state && r.type() !== EmployeeVacationRequestType.SpecialLeave).reduce((s, r) => {
            return s + r.hours();
        }, 0);
    }

    async init() {
        this.isPayrollOrFlexEmployee(false);
        await this.refresh(moment().year());
    }

    async refresh(selectedYear: number) {
        const response = await fetch(this.options.getOverviewLocation, {
            method: 'GET'
        });

        if (response.ok) {
            this.hoursPerYear(response.data.hoursPerYear);
            this.hoursPerWeek(response.data.hoursPerWeek);
            this.hoursWorkWeek(response.data.hoursWorkWeek);
            this.contractStart(moment(response.data.contractStart));
            this.isPayrollOrFlexEmployee(response.data.isPayrollOrFlexEmployee);
            if (response.data.contractEnd) {
                this.contractEnd(moment(response.data.contractEnd));
            }
            this.requests(response.data.requests
                .map((r: any) => new EmployeeVacationRequestModel(r)));

            const map = new Map<number, number>();

            Object.keys(response.data.availableHours).forEach(y => {
                map.set(parseInt(y), response.data.availableHours[y]);
            });

            this.allAvailableHours(map);

            if (response.data.afasBalances != null) {
                this.allAfasVacationBalances(response.data.afasBalances
                    .map((b: any) => new AfasVacationBalanceModel(b)));
                this.hasAfasBalances(true);
            } else {
                this.allAfasVacationBalances(null);
                this.hasAfasBalances(false);
            }


            this.year(selectedYear);
        } else {
            errorToast('Fout bij ophalen data');
        }
    }

    async closePopup() {
        closePopups();
    }

    async updateVacationHoursForCurrentYear() {
        const response = await fetch(`/Employer/EmployeeVacation/UpdateVacationHours/${this.options.employeeId}`);
        if (response.ok) {
            if (this.options.refresh) {
                this.options.refresh();
            }

            this.refresh(moment().year());

            successToast('De nieuwe waarde van de vakantieuren zijn opgeslagen');
        }
        else {
            errorToast('Er ging iets mis met het verzoek');
            if (response.errors) {
                response.errors.forEach(e => console.error(e.message));
            }
        }

        this.closePopup();
    }
}
